import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LanguageContextType, LanguageOption } from "../../types";
import { DEFAULT_LANGUAGE_SETTINGS } from "../configs";
import { useGetKioskSettingQuery } from "../services/kioskApiSlice";
import { getLanguageDisplayLabel } from "../utils";

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: React.ReactNode;
    characterId: string;
    getAvailableLanguageOptions?: (currentIndex: number) => LanguageOption[];
    getUsedLanguagesByOthers?: (currentIndex: number) => string[];
}

export const LanguageProvider = ({
    children,
    characterId,
    getAvailableLanguageOptions,
    getUsedLanguagesByOthers,
}: LanguageProviderProps) => {
    const { t } = useTranslation();
    const { data: kioskSetting, isLoading: isLoadingLanguageOptions } = useGetKioskSettingQuery({
        character_id: characterId,
    });

    // Transform language codes to options with i18n labels
    const languageOptions: LanguageOption[] = useMemo(() => {
        const configuredLanguages = kioskSetting?.data?.native?.general?.languageOptions;
        const fallbackLanguages = DEFAULT_LANGUAGE_SETTINGS.languageOptions;

        const selectedLanguages =
            configuredLanguages && configuredLanguages.length > 0 ? configuredLanguages : fallbackLanguages;

        return selectedLanguages.map((lang) => ({
            label: getLanguageDisplayLabel(lang, t),
            value: lang,
        }));
        // 這邊不將t放進dependencies，是因為他會造成不必要的重複渲染，會影響使用者體驗
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kioskSetting]);

    const value = useMemo(() => {
        const usedLanguagesFn = getUsedLanguagesByOthers || (() => []);
        const availableLanguagesFn =
            getAvailableLanguageOptions ||
            ((currentIndex: number) => {
                const used = usedLanguagesFn(currentIndex);
                return languageOptions.filter((opt) => !used.includes(opt.value));
            });

        return {
            languageOptions,
            isLoadingLanguageOptions,
            getAvailableLanguageOptions: availableLanguagesFn,
            getUsedLanguagesByOthers: usedLanguagesFn,
        };
    }, [languageOptions, isLoadingLanguageOptions, getAvailableLanguageOptions, getUsedLanguagesByOthers]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
};
